// QuestionnaireCheckDisplay.tsx
import React from "react"
import { Loader } from "shared/ui/Loader/Loader.component"
import {
  faCircleCheck,
  faCircleExclamation,
  faClose,
  faRefresh,
  faExclamationTriangle,
  faLocationDot,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useProjectStore } from "stores/projectStore"
import {
  NotificationError,
  NotificationWarning,
  Variant,
} from "types/questionnaire.types"

/**
 * Props interface for the QuestionnaireCheckDisplay component
 */
interface QuestionnaireCheckDisplayProps {
  /** The list of warnings found during the check */
  checkQuestionnaireWarnings?: NotificationWarning[]
  /** The list of errors found during the check */
  checkQuestionnaireErrors?: NotificationError[]
  /** Function to enable or disable the questionnaire check display */
  setCheckingQuestionnaireEnabled: (enabled: boolean) => void
  /** Function to trigger the questionnaire check */
  checkQuestionnaire: (id: string) => Promise<void>
  /** Indicates if the questionnaire is currently being checked */
  isCheckingQuestionnaire: boolean
  /** Function to handle the click event on an error message */
  handleErrorClick: (lineNumber: number) => void
  /** Current variant name */
  currentVariant?: string
  /** Function to switch to a specific variant */
  onSwitchVariant?: (variant: Variant | { name: string }) => void
  /** Available variants */
  variants?: Variant[]
  /** Function to set pending scroll after variant switch */
  setPendingScroll?: (lineNumber: number) => void
}

/**
 * QuestionnaireCheckDisplay component displays the results of a questionnaire check,
 * including any errors or warnings found during the check process.
 *
 * @component
 * @param props - The component props
 * @returns The rendered QuestionnaireCheckDisplay component
 */
const QuestionnaireCheckDisplay: React.FC<QuestionnaireCheckDisplayProps> = ({
  checkQuestionnaireWarnings = [],
  checkQuestionnaireErrors = [],
  setCheckingQuestionnaireEnabled,
  checkQuestionnaire,
  isCheckingQuestionnaire,
  handleErrorClick,
  currentVariant = "",
  onSwitchVariant,
  variants = [],
  setPendingScroll,
}) => {
  const project = useProjectStore((state) => state.project)

  /**
   * Handles the refresh button click to re-check the questionnaire.
   *
   * @function handleRefreshClick
   */
  const handleRefreshClick = (): void => {
    checkQuestionnaire(project.id)
  }

  /**
   * Handles the close button click to hide the questionnaire check display.
   *
   * @function handleCloseClick
   */
  const handleCloseClick = (): void => {
    setCheckingQuestionnaireEnabled(false)
  }

  /**
   * Handles clicking on an error/warning to navigate to the correct variant and line.
   */
  const handleItemClick = (lineNumber: number, variant?: string): void => {
    // Normalize variant names - API may return "MASTER" but we use "" for master
    const targetVariant = variant?.toUpperCase() === "MASTER" ? "" : (variant || "")
    const currentVariantNormalized = currentVariant === "master" ? "" : currentVariant

    // If variant is different, switch first then scroll
    if (targetVariant !== currentVariantNormalized && onSwitchVariant) {
      // Find the full variant object or use Master
      let variantToSwitch: Variant | { name: string }
      if (targetVariant === "") {
        variantToSwitch = { name: "Master" }
      } else {
        // Find variant by name (case-insensitive)
        const fullVariant = variants.find(v => v.name.toLowerCase() === targetVariant.toLowerCase())
        variantToSwitch = fullVariant || { name: targetVariant }
      }
      // Set pending scroll before switching variant
      if (setPendingScroll) {
        setPendingScroll(lineNumber)
      }
      onSwitchVariant(variantToSwitch)
    } else {
      handleErrorClick(lineNumber)
    }
  }

  /**
   * Renders the list of errors found during the questionnaire check.
   *
   * @function renderErrors
   * @returns The list of rendered error elements
   */
  const renderErrors = (): React.ReactNode[] =>
    checkQuestionnaireErrors.map((item: NotificationError, index: number) => (
      <div
        className="flex items-start gap-3 p-3 mb-2 bg-red-50 border border-red-200 rounded-md"
        key={index}
      >
        <FontAwesomeIcon
          icon={faCircleExclamation}
          className="text-red-500 mt-1 flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold text-red-700 text-sm">
              E{item.errcode}
            </span>
            <span
              className="cursor-pointer text-[var(--dq-brand)] hover:underline text-sm flex items-center gap-1"
              onClick={() => handleItemClick(item["line nbr"], item.variant)}
            >
              <FontAwesomeIcon icon={faLocationDot} className="text-xs" />
              Line {item["line nbr"]}
            </span>
            {item.variant && (
              <span className="text-gray-500 text-xs">({item.variant})</span>
            )}
          </div>
          <p className="text-sm text-gray-700 mb-1">{item.message}</p>
          {item.line && (
            <div className="text-xs text-gray-500 font-mono bg-red-100 px-2 py-1 rounded break-all">
              {item.line}
            </div>
          )}
        </div>
      </div>
    ))

  /**
   * Renders the list of warnings found during the questionnaire check.
   *
   * @function renderWarnings
   * @returns The list of rendered warning elements
   */
  const renderWarnings = (): React.ReactNode[] =>
    checkQuestionnaireWarnings.map(
      (item: NotificationWarning, index: number) => (
        <div
          className="flex items-start gap-3 p-3 mb-2 bg-yellow-50 border border-yellow-200 rounded-md"
          key={index}
        >
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="text-yellow-500 mt-1 flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold text-yellow-700 text-sm">
                W{item.errcode}
              </span>
              <span
                className="cursor-pointer text-[var(--dq-brand)] hover:underline text-sm flex items-center gap-1"
                onClick={() => handleItemClick(item["line nbr"], item.variant)}
              >
                <FontAwesomeIcon icon={faLocationDot} className="text-xs" />
                Line {item["line nbr"]}
              </span>
              {item.variant && (
                <span className="text-gray-500 text-xs">({item.variant})</span>
              )}
            </div>
            <p className="text-sm text-gray-700 mb-1">{item.message}</p>
            {item.line && (
              <div className="text-xs text-gray-500 font-mono bg-yellow-100 px-2 py-1 rounded break-all">
                {item.line}
              </div>
            )}
          </div>
        </div>
      )
    )

  return (
    <div className="flex flex-col h-full p-3 overflow-hidden">
      <div className="flex-shrink-0 flex justify-between items-center mb-3 pb-2 border-b border-gray-200">
        <div className="flex items-center gap-2">
          {checkQuestionnaireErrors.length > 0 ||
          checkQuestionnaireWarnings.length > 0 ? (
            <>
              <FontAwesomeIcon
                icon={faCircleExclamation}
                className="text-red-500"
              />
              <span className="font-semibold text-gray-800">
                {checkQuestionnaireErrors.length} Error(s)
              </span>
              <span className="text-gray-400">|</span>
              <span className="font-semibold text-gray-800">
                {checkQuestionnaireWarnings.length} Warning(s)
              </span>
            </>
          ) : (
            <>
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="text-green-500"
              />
              <span className="font-semibold text-gray-800">
                No Issues Found
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <FontAwesomeIcon
            onClick={handleRefreshClick}
            icon={faRefresh}
            className="cursor-pointer text-gray-500 hover:text-[var(--dq-brand)] transition-colors"
          />
          <FontAwesomeIcon
            onClick={handleCloseClick}
            icon={faClose}
            className="cursor-pointer text-gray-500 hover:text-red-500 transition-colors"
          />
        </div>
      </div>
      {isCheckingQuestionnaire ? (
        <div className="flex flex-col items-center justify-center flex-grow">
          <Loader fontSize={24} />
          Checking Questionnaire...
        </div>
      ) : (
        <div className="overflow-y-auto flex-grow">
          {checkQuestionnaireErrors.length > 0 && (
            <>
              <p className="text-md font-semibold">Error(s)</p>
              {renderErrors()}
            </>
          )}
          {checkQuestionnaireWarnings.length > 0 && (
            <>
              <p className="text-md font-semibold">Warning(s)</p>
              {renderWarnings()}
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default QuestionnaireCheckDisplay
